<template>
  <v-dialog
    v-model="dialog"
    persistent
    width="1024"
    @click:outside="dialog = false"
  >
    <template #activator="{ props }">
      <v-btn :color="this.color" variant="tonal" v-bind="props" class="ml-2">{{
        this.text
      }}</v-btn>
    </template>

    <v-card>
      <v-card-title>
        <div class="d-flex justify-space-between">
          <span class="text-h5">Informações sobre a tarefa</span>
          <v-btn
            variant="plain"
            rounded="xl"
            prepend-icon="mdi-window-close"
            @click="dialog = false"
          ></v-btn>
        </div>
      </v-card-title>

      <v-card-text>
        <v-container>
          <v-form ref="form" validate-on="lazy submit">
            <v-row>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="this.prioridade"
                  label="Prioridade da Tarefa*"
                  :items="prioridades"
                  :rules="prioridadeRules"
                ></v-select>
              </v-col>

              <v-col cols="12" sm="6">
                <v-select
                  v-model="this.storypoint"
                  label="Storypoint da Tarefa*"
                  :items="storypoints"
                  :rules="storypointRules"
                ></v-select>
              </v-col>

              <v-col cols="12" sm="6">
                <v-select
                  v-model="this.repositorio"
                  label="Repositorio da Tarefa*"
                  :items="repositoriosNome"
                  :rules="repositorioRules"
                ></v-select>
              </v-col>
            </v-row>
          </v-form>
        </v-container>

        <small>*Campo obrigatório</small>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>

        <v-btn color="blue-darken-1" variant="text" @click="submitForm">
          Salvar
          <v-progress-circular
            v-if="this.isLoading"
            class="ml-2"
            indeterminate
            size="20"
          />
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
/**
 * @module ModalCriarTarefaCorrecao
 * @desc Modal para a criação da tarefa de correção do bug.
 * @vue-data {boolean} [dialog=false] - Flag que indica se o modal
 * está visível.
 * @vue-data {string} [prioridade=''] - A prioridade selecionada.
 * @vue-data {Array.<string>} prioridades - Array com as prioridades
 * possíveis.
 * @vue-data {string} [storypoint=''] - O storypoint selecionado.
 * @vue-data {Array.<string>} storypoints - Array com os storypoints
 * possíveis.
 * @vue-data {string} idProjeto - O id do projeto.
 * @vue-data {boolean} [isLoading=false] - Flag que indica se a
 * tarefa de implementação está sendo criada.
 */
import axios from 'axios';

import store from '@/store/store';
const apiUrl = process.env.VUE_APP_API_URL;

export default {
  props: {
    id: Number,
    color: String,
    text: String,
  },
  emits: ['tarefaCriada'],
  data: () => ({
    dialog: false,
    prioridade: '',
    prioridades: ['URGENTE', 'ALTA', 'MEDIA', 'BAIXA'],
    storypoint: '',
    storypoints: ['0', '0.5', '1', '2', '3', '5', '8', '13', '20', '40'],
    repositorio: '',
    repositorios: [],
    repositoriosNome: [],
    idRepositorio: null,
    idProjeto: null,
    isLoading: false,
    prioridadeRules: [(v) => !!v || 'Prioridade é obrigatória'],
    repositorioRules: [(v) => !!v || 'Repositório é obrigatório'],
    storypointRules: [(v) => !!v || 'Storypoint é obrigatório'],
  }),
  created() {
    this.idProjeto = this.$route.params.idProjeto;
    this.buscarRepositorios();
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeyDown);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
  },
  methods: {
    /**
     * Lida com o evento de pressionar tecla. Se a tecla pressionada for ESC,
     * o modal é fechado.
     *
     * @param {Object} event Objeto do evento
     */
    handleKeyDown(event) {
      if (event.key === 'Escape') {
        this.dialog = false;
      }
    },

    /**
     * Realiza requisição HTTP para buscar os repositorios do projeto.
     */
    async buscarRepositorios() {
      const response = await axios.get(
        `${apiUrl}/projetos/${this.idProjeto}/repositorios/`,
        {
          headers: {
            Authorization: `Bearer ${store.state.token}`,
          },
        },
      );
      this.repositorios = response.data;
      this.repositoriosNome = this.repositorios.map(
        (repositorio) => repositorio.tb_repositorio.nome,
      );
    },

    // busca id do repositorio a partir do repositorio selecionado.
    buscarIdRepositorio() {
      const repositorio = this.repositorios.find(
        (repositorio) => repositorio.tb_repositorio.nome == this.repositorio,
      );
      this.idRepositorio = repositorio.tb_repositorio.id_repositorio;
    },

    /**
     * Realiza requisição HTTP para criar a tarefa de implementação.
     */
    async submitForm() {
      const isValid = (await this.$refs.form.validate()).valid;

      if (isValid) {
        this.isLoading = true;

        if (this.repositorio) {
          this.buscarIdRepositorio();
        }

        const dataTarefa = {
          // eslint-disable-next-line camelcase
          id_bug: this.id,
          titulo: `Correção do bug #${this.id}`,
          descricao: `Esta tarefa tem como objetivo corrigir o bug #${this.id}`,
          storypoint: this.storypoint,
          prioridade: this.prioridade,
          tipo_tarefa: 'CORRECAO_BUG', // eslint-disable-line
          id_projeto: parseInt(this.idProjeto), // eslint-disable-line
          id_repo: parseInt(this.idRepositorio), // eslint-disable-line
        };

        try {
          const resultTarefa = await axios.post(
            apiUrl + '/tarefas/criar',
            dataTarefa,
            {
              headers: { Authorization: `Bearer ${store.state.token}` },
            },
          );

          // Se a tarefa foi cadastrada com sucesso.
          if (resultTarefa) {
            await axios.patch(
              `${apiUrl}/api/v1/bug/${this.id}/atualizar-status`,
              {
                status: 'PARA_FAZER',
              },
              {
                headers: {
                  Authorization: `Bearer ${store.state.token}`,
                },
              },
            );

            this.$toast.success('Bug atualizado!');
            this.$emit('tarefaCriada');
            this.$router.push(`/projects/${this.idProjeto}/Bugs/${this.id}`);
          }
        } catch (error) {
          console.log(error);
          this.$toast.error(error.response.data.msg);
        } finally {
          this.dialog = false;
          this.isLoading = false;
        }
      }
    },
  },
};
</script>
